/**
 * The AI tutor chat panel, shared by the session page (ask about the classes)
 * and the reading page (ask about the book).
 *
 * One conversation per student per module, kept by the tutor on the mentor's
 * laptop. A question is a job: sent, then polled until answered; a question
 * still waiting when the panel closes is picked up again on the next mount.
 * Each question carries whatever the host page says about where the student is
 * (`context()`: the moment of a class, the sections of a book on screen), and
 * the host may hand in a passage the student selected (`quote`); it is sent
 * questions about it.
 *
 * A question can carry up to MAX_IMAGES images (attached or pasted). They are
 * shrunk here first — longest side 1600 px, JPEG — so a few phone photos fit
 * in one request; the tutor sees them, the transcript only notes that there
 * were some ("[2 images attached]").
 */

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import { Box, Button, Chip, CircularProgress, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

import { ConfirmDialog } from "../ContentBank/dialogs";
import { errorMessage } from "../ContentBank/contentBankApi";
import { formatClock } from "../Sessions/SecurePlayer";
import { FONT } from "../Sessions/sessionParts";
import { describeRange, findQuote, flashRange, setHighlights } from "./anchors";

const POLL_START_MS = 1200;
const REVEAL_PAGES = 12;             // pages of history loaded to find a turn a tab branched from
const POLL_MAX_MS = 4000;
const GIVE_UP_MS = 5 * 60 * 1000;
const STATUS_MS = 30000;
const MAX_POLL_MISSES = 10;          // a tunnel blip is not a failed question
const pendingKey = (id) => `tutor-pending:${id}`;

const readPending = (id) => {
  try { return JSON.parse(sessionStorage.getItem(pendingKey(id)) || "null"); } catch { return null; }
};
const writePending = (id, value) => {
  try {
    if (value) sessionStorage.setItem(pendingKey(id), JSON.stringify(value));
    else sessionStorage.removeItem(pendingKey(id));
  } catch { /* storage blocked: the question simply is not resumed */ }
};

export const newRequestId = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`)
    .replace(/[^A-Za-z0-9_-]/g, "");

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const MAX_IMAGES = 4;
const IMAGE_PX = 1600;
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const MAX_SOURCE_BYTES = 20 * 1024 * 1024;   // before shrinking

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error("read"));
  reader.readAsDataURL(file);
});

/** A picked or pasted image as a small JPEG data URL (as-is if the browser can't redraw it). */
export async function shrinkImage(file) {
  const original = await readAsDataUrl(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      setTimeout(() => reject(new Error("slow")), 8000);   // never hang the attach on an odd file
      el.src = original;
    });
    const scale = Math.min(1, IMAGE_PX / Math.max(img.naturalWidth || 1, img.naturalHeight || 1));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";                    // transparent PNGs onto white, not black
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const small = canvas.toDataURL("image/jpeg", 0.85);
    return small.startsWith("data:image/jpeg") && small.length < original.length ? small : original;
  } catch {
    return original;
  }
}

const ATTACHED = /\s*\[(?:image|\d+ images) attached\]$/;
/** "[2 images attached]" — what the transcript keeps of a question's images. */
export const splitAttached = (text = "") => {
  const m = text.match(ATTACHED);
  if (!m) return { text, attached: 0 };
  const n = m[0].match(/\d+/);
  return { text: text.slice(0, m.index), attached: n ? Number(n[0]) : 1 };
};

/** A book source: a section of a chapter of the reading (the 1:1 pipeline's shape). */
export const isBookSource = (s) => !s.kind && typeof s.chapter_index === "number";

export const sourceLabel = (s) => {
  if (s.kind === "recording") return `${(s.label || "Class").split(" · ")[0]} · ${formatClock(s.start)}`;
  if (s.kind === "supplement") return `${(s.file || "").split("/").pop()}${s.part ? ` · ${s.part}` : ""}`;
  if (isBookSource(s)) return `Ch ${s.chapter_index + 1} · ${s.title || s.section || ""}`;
  // The grep agent's source (Study Anything): the lines of the book it read, under a heading.
  if (s.kind === "lines") return `${s.title || "Book"} · L${s.start}${s.end && s.end !== s.start ? `–${s.end}` : ""}`;
  return `Reading · ${s.title || s.section || ""}`;
};

/** Distinct, useful sources: one per class moment / book section / reading chapter / document. */
export const distinctSources = (sources = []) => {
  const seen = new Set();
  return sources.filter((s) => {
    const key = s.kind === "recording" ? `r${s.recording_id}:${Math.floor((s.start || 0) / 60)}`
      : s.kind === "lines" ? `l${s.start}:${s.end}`
        : isBookSource(s) ? `b${s.chapter_index}:${s.section}` : `${s.kind}:${s.file}:${s.title}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 6);
};

const recordingClickable = (s) => s.kind === "recording";

function CopyButton({ text }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { /* clipboard blocked: nothing to do */ }
  };
  return (
    <Tooltip title={done ? "Copied" : "Copy answer"}>
      <IconButton size="small" onClick={copy} aria-label="Copy answer" sx={{ p: 0.25, color: "#9ca3af" }}>
        {done ? <CheckIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
      </IconButton>
    </Tooltip>
  );
}

/**
 * One message. An answer carries `data-turn` (its place in the conversation)
 * so a tab branched from it can be traced back; the tabs branched from it
 * (`anchors`) are drawn as highlights over their words and listed as chips.
 */
export function Message({ m, onSource, isClickable = recordingClickable, anchors = [], onOpenThread }) {
  const mine = m.role === "user";
  const bodyRef = useRef(null);
  const owner = useRef({});
  const anchorKey = anchors.map((a) => `${a.id}:${a.quote}`).join("|");
  useLayoutEffect(() => {
    if (mine) return undefined;
    const ranges = anchors.map((a) => findQuote(bodyRef.current, a)).filter(Boolean);
    setHighlights("tutor-threads", owner.current, ranges);
    const mark = owner.current;
    return () => setHighlights("tutor-threads", mark, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorKey, m.content, mine]);
  return (
    <Box sx={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", mb: 1.25 }}
      data-turn={typeof m.i === "number" ? m.i : undefined} data-role={m.role}>
      <Box sx={{
        maxWidth: "92%", px: 1.5, py: 1, borderRadius: 2, fontFamily: FONT, fontSize: 14, lineHeight: 1.55,
        backgroundColor: mine ? "#0172A6" : "#f3f6f9", color: mine ? "#fff" : "#1f2937",
        "& p": { my: 0.5 }, "& pre": { overflowX: "auto", fontSize: 12.5 }, "& code": { fontSize: 12.5 },
      }}>
        {mine && m.context && (
          <Typography data-testid="tutor-quote" sx={{ fontFamily: FONT, fontSize: 12, opacity: 0.85, mb: 0.5,
            pl: 1, borderLeft: "2px solid rgba(255,255,255,0.6)", fontStyle: "italic", whiteSpace: "pre-wrap" }}>
            {m.context.length > 280 ? `${m.context.slice(0, 280)}…` : m.context}
          </Typography>
        )}
        {mine && m.images?.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
            {m.images.map((src, k) => (
              <Box key={k} component="img" src={src} alt={`Attached image ${k + 1}`}
                sx={{ width: 72, height: 72, objectFit: "cover", borderRadius: 1, border: "1px solid rgba(255,255,255,0.5)" }} />
            ))}
          </Box>
        )}
        {mine ? (
          <Typography sx={{ fontFamily: FONT, fontSize: 14, whiteSpace: "pre-wrap" }}>{splitAttached(m.content).text}</Typography>
        ) : (
          <div ref={bodyRef} data-answer="">
            <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>{m.content}</ReactMarkdown>
          </div>
        )}
        {!mine && anchors.length > 0 && (
          <Box data-testid="tutor-branches" sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
            {anchors.map((a) => (
              <Chip key={a.id} size="small" icon={<CallSplitIcon />} variant="outlined" color="primary"
                label={`${a.title}${a.exchanges ? ` · ${a.exchanges}` : ""}`}
                onClick={onOpenThread ? () => onOpenThread(a.id) : undefined}
                sx={{ fontFamily: FONT, fontSize: 11.5, height: 24, backgroundColor: "#fff", maxWidth: 220 }} />
            ))}
          </Box>
        )}
        {mine && !m.images?.length && splitAttached(m.content).attached > 0 && (
          <Typography sx={{ fontFamily: FONT, fontSize: 11, opacity: 0.85, mt: 0.25 }}>
            📎 {splitAttached(m.content).attached === 1 ? "1 image" : `${splitAttached(m.content).attached} images`}
          </Typography>
        )}
        {mine && m.player && (
          <Typography sx={{ fontFamily: FONT, fontSize: 11, opacity: 0.8, mt: 0.25 }}>at {formatClock(m.player.t)}</Typography>
        )}
        {!mine && (m.sources?.length > 0 || m.content) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75, alignItems: "center" }}>
            {distinctSources(m.sources).map((s, k) => (
              <Chip key={k} size="small" label={sourceLabel(s)} variant="outlined"
                icon={s.kind === "recording" ? <PlayCircleOutlineIcon /> : <MenuBookOutlinedIcon />}
                onClick={onSource && isClickable(s) ? () => onSource(s) : undefined}
                sx={{ fontFamily: FONT, fontSize: 11.5, height: 24, backgroundColor: "#fff" }} />
            ))}
            {m.content && <Box sx={{ ml: "auto" }}><CopyButton text={m.content} /></Box>}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/** The history already holds this pending question (asked at or after it was sent). */
function savedIn(messages, pending) {
  const since = (pending.at || 0) / 1000 - 60;
  return messages.some((m) => m.role === "user" && splitAttached(m.content).text === pending.message
    && (m.timestamp || 0) >= since);
}

const DEFAULT_LABELS = {
  online: "Ask about this module",
  offline: "The tutor is offline right now",
  notReady: "The tutor hasn't been prepared for this module yet",
  empty: "Ask anything about this module.",
  footer: "Your mentor can see this conversation.",
  clearBody: "The tutor starts fresh for this module. Your mentor can still see what was asked.",
};

/**
 * api: { ask(body), job(token), history(before), clear()?, status() }
 * pendingId: where a waiting question is remembered (one per conversation)
 * context(): { payload, bubble } — extra fields for this question, and for its bubble
 * quote: { text, … } a passage the student selected; sent as context_text, cleared after
 * onClearQuote(): the student dismissed the passage
 * Side conversations (TutorTabs):
 *   anchors: the tabs branched from this conversation's answers [{id, parent_turn, quote, prefix, suffix, title, exchanges}]
 *   onBranch({parent_turn, quote, prefix, suffix, around}): the student selected words of an answer to ask about in a new tab
 *   onOpenThread(id): a chip under an answer was clicked
 *   onQuoteBranch(): the selected book passage should open as a tab instead
 *   pinned: { label, quote, onClick } — a tab's own origin, shown under the header; the click goes back there
 *   idle: not the tab on screen — no status polling
 *   onAnswered(): an answer arrived (for an unread mark on a tab not on screen)
 * ref: { revealTurn(i, quote) → Promise<boolean>, focus() }
 */
const TutorChat = forwardRef(function TutorChat({ api, pendingId, context, onSource, isClickable, labels: given,
  quote = null, onClearQuote, height = { xs: 560, md: "100%" },
  anchors = [], onBranch, onOpenThread, onQuoteBranch, pinned = null, idle = false, onAnswered }, ref) {
  const labels = { ...DEFAULT_LABELS, ...given };
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]); // [{id, url}]
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  const [waiting, setWaiting] = useState(null); // {state, position}
  const [error, setError] = useState("");
  const [online, setOnline] = useState(true);
  const [notReady, setNotReady] = useState(false);
  const nearBottom = useRef(true);
  const [confirmClear, setConfirmClear] = useState(false);
  const boxRef = useRef(null);
  const keepScroll = useRef(null);
  const alive = useRef(true);
  const apiRef = useRef(api);
  apiRef.current = api;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const hasMoreRef = useRef(false);
  hasMoreRef.current = hasMore;
  const loadingRef = useRef(true);
  loadingRef.current = loading;
  const busyEarlier = useRef(false);
  const [branchAt, setBranchAt] = useState(null);   // {top, left, anchor, turn}: the "Ask in a new tab" button
  const onAnsweredRef = useRef(onAnswered);
  onAnsweredRef.current = onAnswered;

  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = await apiRef.current.history(null);
      if (!alive.current) return;
      const pending = pendingId ? readPending(pendingId) : null;
      // A question still waiting is not in the history yet: keep its bubble — unless it
      // was answered while the panel was closed and the history already has it.
      const saved = pending && savedIn(page.messages, pending);
      setMessages((cur) => [...page.messages.map((m, n) => ({ ...m, id: m.i ?? `h${n}` })),
        ...cur.filter((m) => pending && !saved && (m.id === `q-${pending.requestId}` || m.id === `a-${pending.requestId}`))]);
      setHasMore(page.has_more);
      if (page.offline) setOnline(false);
      setNotReady(!!page.not_ready);
    } catch (e) {
      if (alive.current) setError(errorMessage(e, "Couldn't load the conversation"));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [pendingId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (idle) return undefined;                       // a tab not on screen leaves the status to the one that is
    const check = () => apiRef.current.status().then((s) => alive.current && setOnline(!!s.online)).catch(() => {});
    check();
    const timer = setInterval(check, STATUS_MS);
    return () => clearInterval(timer);
  }, [pendingId, idle]);

  // A passage was just selected: put the cursor in the box, ready to ask about it.
  useEffect(() => { if (quote) inputRef.current?.focus({ preventScroll: true }); }, [quote]);

  // Newest at the bottom — unless they scrolled up to read; and after loading an
  // earlier page, stay where you were.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    if (keepScroll.current !== null) {
      box.scrollTop = box.scrollHeight - keepScroll.current;
      keepScroll.current = null;
    } else if (nearBottom.current) {
      box.scrollTop = box.scrollHeight;
    }
  }, [messages, waiting]);

  const onScroll = () => {
    const box = boxRef.current;
    if (box) nearBottom.current = box.scrollHeight - box.scrollTop - box.clientHeight < 80;
    if (branchAt) setBranchAt(null);
  };

  const loadEarlier = useCallback(async () => {
    const first = messagesRef.current.find((m) => typeof m.i === "number")?.i ?? null;
    if (first === null || busyEarlier.current) return false;
    busyEarlier.current = true;
    setLoadingEarlier(true);
    try {
      const page = await apiRef.current.history(first);
      if (!alive.current) return false;
      keepScroll.current = boxRef.current ? boxRef.current.scrollHeight - boxRef.current.scrollTop : null;
      const fresh = page.messages.map((m, n) => ({ ...m, id: m.i ?? `e${first}-${n}` }));
      setMessages((cur) => {
        const have = new Set(cur.map((m) => m.id));
        return [...fresh.filter((m) => !have.has(m.id)), ...cur];
      });
      hasMoreRef.current = !!page.has_more;
      setHasMore(!!page.has_more);
      return fresh.length > 0;
    } catch (e) {
      setError(errorMessage(e, "Couldn't load earlier messages"));
      return false;
    } finally {
      busyEarlier.current = false;
      if (alive.current) setLoadingEarlier(false);
    }
  }, []);

  /** Scroll to turn `i` (loading earlier pages if it is further back) and flash it — and its quoted words. */
  const revealTurn = useCallback(async (i, quoteText = "") => {
    const find = () => boxRef.current?.querySelector(`[data-turn="${i}"]`);
    // The first page may still be on its way (a tab opened before its parent was read).
    for (let n = 0; loadingRef.current && n < 100; n += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 50));
    }
    let el = find();
    for (let n = 0; !el && n < REVEAL_PAGES && hasMoreRef.current; n += 1) {
      // eslint-disable-next-line no-await-in-loop
      const more = await loadEarlier();
      if (!more) break;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 30));     // let React paint the page
      el = find();
    }
    if (!el) return false;
    nearBottom.current = false;
    el.scrollIntoView?.({ behavior: "smooth", block: "center" });
    if (el.animate) {
      el.animate([{ backgroundColor: "rgba(245,158,11,0.18)" }, { backgroundColor: "transparent" }],
        { duration: 1800, easing: "ease-out" });
    }
    if (quoteText) flashRange(findQuote(el.querySelector("[data-answer]") || el, { quote: quoteText }));
    return true;
  }, [loadEarlier]);

  /** An exchange answered elsewhere (a spoken doubt, say): shown here, numbered like the history will number it. */
  const append = useCallback(({ role, content, ...rest }) => {
    setMessages((cur) => {
      const last = cur.reduce((top, m) => (typeof m.i === "number" && m.i > top ? m.i : top), -1);
      return [...cur, { id: `x-${newRequestId()}`, role, content, i: last + 1, ...rest }];
    });
    nearBottom.current = true;
  }, []);

  useImperativeHandle(ref, () => ({
    revealTurn,
    focus: () => inputRef.current?.focus({ preventScroll: true }),
    append,
    addImages: (files) => addImagesRef.current(files),
    getMessages: () => messagesRef.current,
  }), [revealTurn, append]);

  /** Words of an answer were selected: offer to ask about them in a new tab. */
  const onPick = () => {
    if (!onBranch) return;
    const sel = window.getSelection?.();
    if (!sel || sel.isCollapsed || !sel.rangeCount) { if (branchAt) setBranchAt(null); return; }
    const range = sel.getRangeAt(0);
    const node = range.commonAncestorContainer;
    const el = (node.nodeType === 1 ? node : node.parentElement)?.closest?.("[data-turn][data-role='assistant']");
    const box = boxRef.current;
    if (!el || !box || !box.contains(el)) { if (branchAt) setBranchAt(null); return; }
    const anchor = describeRange(range, el.querySelector("[data-answer]") || el);
    const turn = Number(el.getAttribute("data-turn"));
    if (!anchor || !Number.isInteger(turn)) { if (branchAt) setBranchAt(null); return; }
    const r = range.getBoundingClientRect ? range.getBoundingClientRect() : { bottom: 0, left: 0 };
    const b = box.getBoundingClientRect();
    setBranchAt({ anchor, turn, top: r.bottom - b.top + box.scrollTop + 6,
      left: Math.max(8, Math.min(r.left - b.left, Math.max(8, b.width - 170))) });
  };

  const branch = () => {
    if (!branchAt || !onBranch) return;
    onBranch({ ...branchAt.anchor, parent_turn: branchAt.turn });
    window.getSelection?.()?.removeAllRanges?.();
    setBranchAt(null);
  };

  /** Poll a job until it settles. A blip is retried; only a lost or failed job ends it. */
  const follow = useCallback(async (pending) => {
    setWaiting({ state: "queued", position: 0 });
    nearBottom.current = true;
    try {
      const started = pending.at || Date.now();
      let delay = POLL_START_MS;
      let misses = 0;
      for (;;) {
        await sleep(delay);
        if (!alive.current) return;                 // still in sessionStorage: picked up on the next mount
        let job;
        try {
          job = await apiRef.current.job(pending.job);
          misses = 0;
        } catch (e) {
          const status = e.response?.status;
          if (status === 404 || status === 403) throw e;
          if (++misses > MAX_POLL_MISSES) throw new Error("Lost contact with the tutor. Your answer will be here when you reopen the chat.");
          continue;
        }
        if (job.state === "done") {
          setMessages((cur) => {
            const mine = (m) => m.id === `q-${pending.requestId}` || m.id === `a-${pending.requestId}`;
            // Already loaded from the history (answered while the panel was closed): don't show it twice.
            if (savedIn(cur.filter((m) => !mine(m)), pending)) return cur.filter((m) => !mine(m));
            // The tutor says which turn the answer is (so a tab can branch from it right
            // away, numbered exactly as the history numbers it); an older tutor leaves it
            // out, and then the exchange takes the next two numbers after what is loaded.
            const last = cur.reduce((top, m) => (typeof m.i === "number" && m.i > top ? m.i : top), -1);
            const at = Number.isInteger(job.turn) && job.turn >= 1 ? job.turn : last + 2;
            return [...cur.filter((m) => m.id !== `a-${pending.requestId}` && m.i !== at && m.i !== at - 1)
              .map((m) => (m.id === `q-${pending.requestId}` && typeof m.i !== "number" ? { ...m, i: at - 1 } : m)),
            { id: `a-${pending.requestId}`, role: "assistant", content: job.answer, sources: job.sources, i: at }];
          });
          onAnsweredRef.current?.();
          break;
        }
        if (job.state === "failed") throw new Error(job.error?.message || "The tutor couldn't answer that. Please try again.");
        setWaiting({ state: job.state, position: job.position || 0 });
        if (Date.now() - started > GIVE_UP_MS) throw new Error("The tutor is taking unusually long. Your answer will be here when you reopen the chat.");
        delay = Math.min(POLL_MAX_MS, delay + 400);
      }
      writePending(pendingId, null);
    } catch (e) {
      writePending(pendingId, null);
      if (alive.current) setError(e.message && !e.response ? e.message : errorMessage(e, "The tutor couldn't answer that"));
    } finally {
      if (alive.current) setWaiting(null);
    }
  }, [pendingId]);

  // A question still waiting when the panel was last closed: keep following it.
  useEffect(() => {
    const pending = pendingId ? readPending(pendingId) : null;
    if (pending?.job && Date.now() - (pending.at || 0) < GIVE_UP_MS) {
      setMessages((cur) => (cur.some((m) => m.id === `q-${pending.requestId}`) ? cur
        : [...cur, { id: `q-${pending.requestId}`, role: "user", content: pending.message || "", ...(pending.bubble || {}) }]));
      follow(pending);
    }
    else if (pending) writePending(pendingId, null);
  }, [pendingId, follow]);

  const addImages = async (files) => {
    const picked = [...(files || [])].filter((f) => IMAGE_TYPES.includes(f.type));
    if (!picked.length) {
      if (files?.length) setError("Attach PNG, JPEG, WebP or GIF images.");
      return;
    }
    const room = MAX_IMAGES - attachments.length;
    if (picked.length > room) setError(`You can attach up to ${MAX_IMAGES} images to one question.`);
    else setError("");
    const take = picked.slice(0, Math.max(0, room)).filter((f) => {
      if (f.size <= MAX_SOURCE_BYTES) return true;
      setError("That image is too large — please keep it under 20 MB.");
      return false;
    });
    try {
      const urls = await Promise.all(take.map(shrinkImage));
      if (!alive.current) return;
      setAttachments((cur) => [...cur, ...urls.map((url) => ({ id: newRequestId(), url }))].slice(0, MAX_IMAGES));
    } catch {
      setError("That image couldn't be read. Try another one.");
    }
  };

  const addImagesRef = useRef(addImages);
  addImagesRef.current = addImages;

  const onPaste = (e) => {
    const files = [...(e.clipboardData?.files || [])];
    if (files.some((f) => IMAGE_TYPES.includes(f.type))) {
      e.preventDefault();
      addImages(files);
    }
  };

  const send = async (asked = null) => {
    const images = attachments.map((a) => a.url);
    const typed = asked ?? input.trim();
    const message = typed || (images.length ? (images.length > 1 ? "Explain these images." : "Explain this image.")
      : quote ? "Explain this passage." : "");
    if (!message || waiting) return;
    const extra = context ? context() || {} : {};
    const quoteText = quote?.text || "";
    const payload = { ...(extra.payload || {}), ...(quoteText ? { context_text: quoteText } : {}) };
    const bubble = { ...(extra.bubble || {}), ...(quoteText ? { context: quoteText } : {}) };
    const requestId = newRequestId();
    if (asked === null) setInput("");
    setAttachments([]);
    setError("");
    if (quote && onClearQuote) onClearQuote();
    nearBottom.current = true;
    setMessages((cur) => [...cur, { id: `q-${requestId}`, role: "user", content: message, images, ...bubble }]);
    setWaiting({ state: "queued", position: 0 });
    let answer;
    try {
      for (let attempt = 0; ; attempt += 1) {
        try {
          answer = await apiRef.current.ask({ message, request_id: requestId, ...payload,
            ...(images.length ? { images } : {}) });
          break;
        } catch (e) {
          // A network blip: the same request id makes a resend the same question.
          if (e.response || attempt >= 2) throw e;
          await sleep(1500);
        }
      }
    } catch (e) {
      if (alive.current) {
        setError(errorMessage(e, "The tutor couldn't take that question"));
        setWaiting(null);
        // Give the question back to edit and resend, images included.
        setMessages((cur) => cur.filter((m) => m.id !== `q-${requestId}`));
        if (asked === null) setInput(typed);
        setAttachments(images.map((url) => ({ id: newRequestId(), url })));
      }
      return;
    }
    const pending = { job: answer.job, requestId, at: Date.now(), message, bubble };
    writePending(pendingId, pending);
    await follow(pending);
  };

  const clear = async () => {
    try {
      await apiRef.current.clear();
      setMessages([]);
      setHasMore(false);
    } catch (e) {
      setError(errorMessage(e, "Couldn't clear the conversation"));
    } finally {
      setConfirmClear(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height }}>
      <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #eef0f2" }}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: online ? "#16a34a" : "#9ca3af" }} />
        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: "#6b7280", flex: 1 }}>
          {notReady ? labels.notReady : online ? labels.online : labels.offline}
        </Typography>
        {messages.length > 0 && api.clear && (
          <Tooltip title="Clear conversation">
            <span>
              <IconButton size="small" onClick={() => setConfirmClear(true)} disabled={!!waiting}
                aria-label="Clear conversation">
                <DeleteSweepOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      {pinned && (
        <Box role={pinned.onClick ? "button" : undefined} tabIndex={pinned.onClick ? 0 : undefined} data-testid="tutor-pinned"
          onClick={pinned.onClick} onKeyDown={(e) => { if (e.key === "Enter" && pinned.onClick) pinned.onClick(); }}
          title={pinned.onClick ? "Back to where this branched from" : undefined}
          sx={{ px: 2, py: 0.75, display: "flex", gap: 0.75, alignItems: "flex-start", borderBottom: "1px solid #eef0f2",
            backgroundColor: "#f8fbfd", cursor: pinned.onClick ? "pointer" : "default", "&:hover": { backgroundColor: "#f1f7fb" } }}>
          <CallSplitIcon sx={{ fontSize: 16, color: "#0172A6", mt: "2px" }} />
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: "#374151", lineHeight: 1.4,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            <Box component="span" sx={{ color: "#6b7280" }}>From {pinned.label}: </Box>
            <Box component="span" sx={{ fontStyle: "italic" }}>“{pinned.quote}”</Box>
          </Typography>
        </Box>
      )}

      <Box ref={boxRef} onScroll={onScroll} onMouseUp={onPick} onKeyUp={onPick} onTouchEnd={() => setTimeout(onPick, 350)}
        data-testid="tutor-messages" sx={{ flex: 1, overflowY: "auto", px: 1.5, pt: 1, position: "relative" }}>
        {branchAt && (
          <Button size="small" variant="contained" startIcon={<CallSplitIcon />} onMouseDown={(e) => e.preventDefault()}
            onClick={branch} data-testid="tutor-branch"
            sx={{ position: "absolute", top: branchAt.top, left: branchAt.left, zIndex: 2, textTransform: "none",
              fontFamily: FONT, fontSize: 12, py: 0.25, boxShadow: 2, whiteSpace: "nowrap" }}>
            Ask in a new tab
          </Button>
        )}
        {hasMore && (
          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Button size="small" onClick={loadEarlier} disabled={loadingEarlier} sx={{ textTransform: "none", fontSize: 12 }}>
              {loadingEarlier ? "Loading…" : "Load earlier messages"}
            </Button>
          </Box>
        )}
        {loading && <Box sx={{ textAlign: "center", mt: 4 }}><CircularProgress size={22} /></Box>}
        {!loading && messages.length === 0 && (
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: "#6b7280", textAlign: "center", mt: 4, px: 2 }}>
            {labels.empty}
          </Typography>
        )}
        {messages.map((m) => (
          <Message key={m.id} m={m} onSource={onSource} isClickable={isClickable} onOpenThread={onOpenThread}
            anchors={typeof m.i === "number" && m.role !== "user" ? anchors.filter((a) => a.parent_turn === m.i) : []} />
        ))}
        {waiting && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#6b7280", mb: 1.5 }}>
            <CircularProgress size={14} />
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5 }}>
              {waiting.state === "queued" && waiting.position > 0 ? `In line — ${waiting.position} ahead of you` : "Thinking…"}
            </Typography>
          </Box>
        )}
      </Box>

      {error && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: "#b42318", px: 2, pb: 0.5 }}>{error}</Typography>}
      <Box sx={{ px: 1.5, pt: 1, pb: 1.25, borderTop: "1px solid #eef0f2" }}>
        {quote && (
          <Box data-testid="tutor-selection" sx={{ mb: 1, p: 1, borderRadius: 1.5, backgroundColor: "#f3f8fb",
            border: "1px solid #d6e8f2" }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
              <FormatQuoteIcon sx={{ fontSize: 16, color: "#0172A6", mt: 0.25 }} />
              <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: "#374151", flex: 1, fontStyle: "italic",
                maxHeight: 72, overflowY: "auto", whiteSpace: "pre-wrap" }}>
                {quote.text}
              </Typography>
              <IconButton size="small" aria-label="Remove the selected passage" onClick={onClearQuote} sx={{ p: 0.25 }}>
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
            {onQuoteBranch && (
              <Button size="small" startIcon={<CallSplitIcon />} onClick={onQuoteBranch} data-testid="tutor-quote-branch"
                sx={{ textTransform: "none", fontFamily: FONT, fontSize: 12, mt: 0.25, py: 0, minHeight: 26 }}>
                Ask in a new tab
              </Button>
            )}
          </Box>
        )}
        {attachments.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }} data-testid="tutor-attachments">
            {attachments.map((a, k) => (
              <Box key={a.id} sx={{ position: "relative" }}>
                <Box component="img" src={a.url} alt={`Image ${k + 1} to send`}
                  sx={{ width: 56, height: 56, objectFit: "cover", borderRadius: 1.5, border: "1px solid #e5e7eb", display: "block" }} />
                <IconButton size="small" aria-label={`Remove image ${k + 1}`}
                  onClick={() => setAttachments((cur) => cur.filter((x) => x.id !== a.id))}
                  sx={{ position: "absolute", top: -8, right: -8, width: 20, height: 20, backgroundColor: "#111827", color: "#fff",
                    "&:hover": { backgroundColor: "#374151" } }}>
                  <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-end" }}>
          <input ref={fileRef} type="file" hidden multiple accept={IMAGE_TYPES.join(",")} data-testid="tutor-image-input"
            onChange={(e) => { addImages(e.target.files); e.target.value = ""; }} />
          <Tooltip title={attachments.length >= MAX_IMAGES ? `Up to ${MAX_IMAGES} images` : "Attach images"}>
            <span>
              <IconButton onClick={() => fileRef.current?.click()} disabled={attachments.length >= MAX_IMAGES || !!waiting}
                aria-label="Attach images" sx={{ mb: 0.25 }}>
                <AddPhotoAlternateOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <TextField fullWidth multiline maxRows={5} size="small" value={input} inputRef={inputRef}
            placeholder={quote ? "Ask about the selected passage…" : attachments.length ? "Ask about these images…" : "Ask a question…"}
            onChange={(e) => setInput(e.target.value)} onPaste={onPaste}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            inputProps={{ "aria-label": "Your question", maxLength: 4000 }}
            sx={{ "& .MuiInputBase-root": { fontFamily: FONT, fontSize: 14 } }} />
          <IconButton color="primary" onClick={() => send()} disabled={(!input.trim() && !attachments.length && !quote) || !!waiting}
            aria-label="Send">
            <SendIcon />
          </IconButton>
        </Box>
        <Typography sx={{ fontFamily: FONT, fontSize: 11, color: "#9ca3af", mt: 0.5 }}>{labels.footer}</Typography>
      </Box>

      <ConfirmDialog open={confirmClear} title="Clear this conversation?" body={labels.clearBody}
        confirmLabel="Clear" onClose={() => setConfirmClear(false)} onConfirm={clear} />
    </Box>
  );
});

export default TutorChat;
